import { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, Loader2 } from 'lucide-react';
import AlumnoRegisterForm from '@/components/AlumnoRegisterForm';
import api from '@/lib/api';
import { cn } from '@/lib/utils';

function parseCode(raw) {
    let v = raw.trim().toLowerCase();
    if (v.includes('/')) {
        v = v.split('?')[0].split('/').filter(Boolean).pop() || '';
    }
    return v;
}

export default function Register() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [tab, setTab] = useState(searchParams.get('rol') === 'owner' ? 'owner' : 'alumno');

    // Owner form state
    const [form, setForm] = useState({
        name: '',
        email: '',
        dni: '',
        phone: '',
        password: '',
        password_confirmation: '',
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    // Alumno invite-code state
    const [code, setCode] = useState(searchParams.get('codigo') || '');
    const [gym, setGym] = useState(null);
    const [codeError, setCodeError] = useState('');
    const [checkingCode, setCheckingCode] = useState(false);
    const [alumnoDone, setAlumnoDone] = useState(false);

    const parsedCode = parseCode(code);

    useEffect(() => {
        setGym(null);
        setCodeError('');
        if (parsedCode.length < 4) return undefined;

        let cancelled = false;
        setCheckingCode(true);
        const timer = setTimeout(async () => {
            try {
                const res = await api.get(`/public/gym-by-invite/${encodeURIComponent(parsedCode)}`);
                if (!cancelled) setGym(res.data.data);
            } catch {
                if (!cancelled) setCodeError('Código inválido. Revisalo o pedíselo nuevamente a tu gimnasio.');
            } finally {
                if (!cancelled) setCheckingCode(false);
            }
        }, 500);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [parsedCode]);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await api.post('/auth/register', form);
            setSuccess(true);
        } catch (err) {
            const msg = err.response?.data?.message;
            const errors = err.response?.data?.errors;
            if (errors) {
                setError(Object.values(errors).flat().join(' '));
            } else {
                setError(msg || 'Error al registrarse.');
            }
        } finally {
            setLoading(false);
        }
    };

    const navbar = (
        <nav className="fixed top-0 left-0 right-0 z-50 bg-black/20">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-white">
                        <path d="M12 2l8.66 5v10l-8.66 5-8.66-5V7z" />
                        <path d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
                    </svg>
                    <span className="text-sm font-bold uppercase tracking-[0.2em]">GYMS.ar</span>
                </Link>
                <div className="flex items-center gap-2 sm:gap-5">
                    <Link to="/login" className="hidden sm:flex items-center text-white/90 hover:text-white transition-colors uppercase text-xs tracking-[0.15em] font-medium">
                        Ingresar
                    </Link>
                </div>
            </div>
        </nav>
    );

    if (success) {
        return (
            <div className="min-h-screen bg-black">
                {navbar}
                <div className="min-h-screen flex items-center justify-center px-4 pt-20">
                    <Card className="w-full max-w-md bg-[#0a0a0a] border-hairline-dark">
                        <CardHeader className="text-center">
                            <CardTitle className="text-2xl font-bold text-white uppercase tracking-wide">Registro exitoso</CardTitle>
                            <CardDescription className="text-white/60">Tu cuenta fue creada correctamente.</CardDescription>
                        </CardHeader>
                        <CardContent className="text-center space-y-4">
                            <p className="text-sm text-white/60">
                                Ahora podés iniciar sesión para acceder a tu panel de propietario.
                            </p>
                            <Button onClick={() => navigate('/login')} className="w-full">
                                Iniciar sesión
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    const tabButton = (value, label) => (
        <button
            type="button"
            onClick={() => setTab(value)}
            className={cn(
                'flex-1 rounded-md px-3 py-2 text-xs font-bold uppercase tracking-[0.15em] transition-colors',
                tab === value ? 'bg-white text-black' : 'text-white/60 hover:text-white'
            )}
        >
            {label}
        </button>
    );

    return (
        <div className="min-h-screen bg-black">
            {navbar}
            <div className="min-h-screen flex items-center justify-center px-4 py-24">
                <div className="w-full max-w-lg">
                    {!alumnoDone && (
                        <div className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-white/5 border border-hairline-dark mb-4">
                            {tabButton('alumno', 'Soy alumno')}
                            {tabButton('owner', 'Soy dueño')}
                        </div>
                    )}

                    {tab === 'alumno' ? (
                        <div className="space-y-4">
                            {!gym ? (
                                <Card className="bg-[#0a0a0a] border-hairline-dark">
                                    <CardHeader className="text-center">
                                        <CardTitle className="text-2xl font-bold text-white uppercase tracking-wide">Crear cuenta</CardTitle>
                                        <CardDescription className="text-white/60">
                                            Ingresá el código de invitación de tu gimnasio.
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="invite-code" className="text-white">Código de invitación</Label>
                                            <div className="relative">
                                                <Input
                                                    id="invite-code"
                                                    value={code}
                                                    onChange={(e) => setCode(e.target.value)}
                                                    placeholder="Ej: k7p2m9xq o link de invitación"
                                                    autoFocus
                                                    className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark pr-9"
                                                />
                                                {checkingCode && (
                                                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-white/50" />
                                                )}
                                            </div>
                                            {codeError ? (
                                                <p className="text-xs text-red-400">{codeError}</p>
                                            ) : (
                                                <p className="text-xs text-white/40">
                                                    Pedile el código o el link de invitación a tu gimnasio. Podés pegar el link completo.
                                                </p>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            ) : (
                                <>
                                    {!alumnoDone && (
                                        <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-white/20 bg-white/5">
                                            <div className="flex items-center gap-3 min-w-0">
                                                {gym.logo ? (
                                                    <img src={gym.logo} alt={gym.name} className="h-9 w-9 rounded-md object-contain bg-white/10 border border-hairline-dark shrink-0" />
                                                ) : (
                                                    <div className="h-9 w-9 rounded-md bg-white/10 flex items-center justify-center shrink-0">
                                                        <Building2 className="h-4 w-4 text-white" />
                                                    </div>
                                                )}
                                                <div className="min-w-0">
                                                    <p className="text-[10px] text-white/60 uppercase tracking-widest">Te estás uniendo a</p>
                                                    <p className="text-sm font-bold text-white truncate">{gym.name}</p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => { setCode(''); setGym(null); }}
                                                className="text-xs text-white/60 hover:text-white underline shrink-0"
                                            >
                                                Cambiar código
                                            </button>
                                        </div>
                                    )}
                                    <AlumnoRegisterForm token={parsedCode} gymName={gym.name} onSuccess={() => setAlumnoDone(true)} />
                                </>
                            )}
                        </div>
                    ) : (
                        <Card className="bg-[#0a0a0a] border-hairline-dark">
                            <CardHeader className="text-center">
                                <CardTitle className="text-2xl font-bold text-white uppercase tracking-wide">Crear cuenta</CardTitle>
                                <CardDescription className="text-white/60">Registrate como propietario de gimnasio</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    {error && (
                                        <div className="bg-[#0a0a0a] text-white text-sm p-3 rounded-md border border-white/20">
                                            {error}
                                        </div>
                                    )}
                                    <div className="space-y-2">
                                        <Label htmlFor="name" className="text-white">Nombre completo</Label>
                                        <Input
                                            id="name"
                                            name="name"
                                            placeholder="Juan Pérez"
                                            value={form.name}
                                            onChange={handleChange}
                                            required
                                            className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="email" className="text-white">Email</Label>
                                        <Input
                                            id="email"
                                            name="email"
                                            type="email"
                                            placeholder="tu@email.com"
                                            value={form.email}
                                            onChange={handleChange}
                                            required
                                            className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="dni" className="text-white">DNI</Label>
                                        <Input
                                            id="dni"
                                            name="dni"
                                            placeholder="12345678"
                                            value={form.dni}
                                            onChange={handleChange}
                                            className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="phone" className="text-white">Teléfono</Label>
                                        <Input
                                            id="phone"
                                            name="phone"
                                            placeholder="1155001234"
                                            value={form.phone}
                                            onChange={handleChange}
                                            className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="password" className="text-white">Contraseña</Label>
                                        <Input
                                            id="password"
                                            name="password"
                                            type="password"
                                            placeholder="••••••••"
                                            value={form.password}
                                            onChange={handleChange}
                                            minLength={8}
                                            required
                                            className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="password_confirmation" className="text-white">Confirmar contraseña</Label>
                                        <Input
                                            id="password_confirmation"
                                            name="password_confirmation"
                                            type="password"
                                            placeholder="••••••••"
                                            value={form.password_confirmation}
                                            onChange={handleChange}
                                            minLength={8}
                                            required
                                            className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark"
                                        />
                                    </div>
                                    <Button type="submit" className="w-full" disabled={loading}>
                                        {loading ? 'Registrando...' : 'Registrarme'}
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>
                    )}

                    <div className="mt-4 text-center text-sm">
                        <span className="text-white/60">¿Ya tenés una cuenta? </span>
                        <Link to="/login" className="text-white underline">
                            Iniciar sesión
                        </Link>
                    </div>
                    <div className="mt-2 text-center">
                        <Link to="/" className="text-sm text-white/60 hover:text-white underline">
                            ← Volver al inicio
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
